import React, { useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { useAsync } from 'react-use';
import { BookApi } from '../../API/BookApi';
import { StorylineApi } from '../../API/StorylineApi';
import { useAuth } from '../../Auth/AuthProvider';
import Button from '../../Components/Button';
import LoadingIcon from '../../Components/LoadingIcon';
import { AnalyticsEvent, useAnalytics } from '../../Context/AnalyticsProvider';
import { Book } from '../../Types/Book';
import { BookChapter } from '../../Types/BookChapter';
import {
  StorylineDilemma,
  StorylineDilemmaChoiceInput,
} from '../../Types/StorylineDilemma';
import StorylineDilemmaOptionsSelectView from '../Controls/StorylineDilemmaOptionsSelectView';

interface ChapterBuilderInlineViewProps {
  book: Book;
  lastChapter: BookChapter;
  allowCreate?: boolean;
  submitButtonText?: string;
  preSubmitPrompt?: {
    title: string;
    description: string;
  };
  onChapterGenerationStarting?: () => void;
  onChapterGenerationStarted?: (chapterId: string, requestId?: string) => void;
  onChapterGenerationError?: () => void;
}

const ChapterBuilderInlineView: React.FC<ChapterBuilderInlineViewProps> = ({
  book,
  lastChapter,
  allowCreate = true,
  submitButtonText = `Generate Chapter ${lastChapter.chapterNumber + 1}`,
  preSubmitPrompt,
  onChapterGenerationStarting,
  onChapterGenerationStarted,
  onChapterGenerationError,
}) => {
  const { trackEvent } = useAnalytics();
  const { isAuthenticated, signUpAsAnonymous } = useAuth();
  const [
    selectedDilemmaOptionByDilemmaId,
    setSelectedDilemmaOptionByDilemmaId,
  ] = useState<Record<string, string>>({});
  const [isGenerating, setIsGenerating] = useState(false);

  const { value: dilemmas, loading: isDilemmasLoading } = useAsync(async () => {
    if (!book || !lastChapter) return undefined;

    const dilemmaApi = new StorylineApi();
    return await dilemmaApi.getChapterDilemmas(book.id, lastChapter.id);
  }, [book, lastChapter]);

  const dilemmaChoices = useMemo<StorylineDilemmaChoiceInput[]>(() => {
    return dilemmas
      ?.filter(
        (dilemma: StorylineDilemma) =>
          selectedDilemmaOptionByDilemmaId[dilemma.id],
      )
      .map((dilemma: StorylineDilemma) => ({
        dilemmaId: dilemma.id,
        chosenOptionId: selectedDilemmaOptionByDilemmaId[dilemma.id],
      }));
  }, [dilemmas, selectedDilemmaOptionByDilemmaId]);

  const canSubmit = useMemo(() => {
    if (!dilemmas) return false;
    return dilemmas.every(
      (dilemma: StorylineDilemma) =>
        selectedDilemmaOptionByDilemmaId[dilemma.id],
    );
  }, [dilemmas, selectedDilemmaOptionByDilemmaId]);

  const handleOptionSelect = (dilemmaId: string, optionId: string) => {
    setSelectedDilemmaOptionByDilemmaId((prev) => ({
      ...prev,
      [dilemmaId]: optionId,
    }));
  };

  async function onSubmit() {
    try {
      setIsGenerating(true);

      // Track finalized dilemma choices
      if (dilemmas) {
        for (const dilemma of dilemmas) {
          const selectedOptionId = selectedDilemmaOptionByDilemmaId[dilemma.id];
          if (selectedOptionId) {
            // Find the selected option from the dilemma options
            const selectedOption = dilemma.options?.find(
              (option) => option.id === selectedOptionId,
            );

            await trackEvent(AnalyticsEvent.DILEMMA_CHOICE_SUBMITTED, {
              source: 'ChapterBuilderInlineView',
              book: book,
              chapter: lastChapter,
              dilemmaId: dilemma.id,
              dilemmaQuestion: dilemma.question,
              selectedOptionId: selectedOptionId,
              selectedDilemmaOption: selectedOption,
            });
          }
        }
      }

      await trackEvent(AnalyticsEvent.CHAPTER_BUILDER_GENERATION_REQUESTED, {
        source: 'ChapterBuilderInlineView',
        book: book,
        chapter: lastChapter,
        selectedDilemmaOptions: selectedDilemmaOptionByDilemmaId,
        dilemmas: dilemmas,
      });

      if (!isAuthenticated) {
        await signUpAsAnonymous();
      }

      const bookApi = new BookApi();

      onChapterGenerationStarting?.();
      const response = await bookApi.triggerAsyncNextChapterGeneration({
        bookId: book.id,
        dilemmaChoices,
      });

      await trackEvent(
        AnalyticsEvent.BOOK_BUILDER_GENERATION_REQUEST_COMPLETED,
        {
          source: 'ChapterBuilderInlineView',
          book: book,
          chapter: lastChapter,
          selectedDilemmaOptions: selectedDilemmaOptionByDilemmaId,
          dilemmas: dilemmas,
          newChapterId: response.newChapterId,
          requestId: response.requestId,
        },
      );

      onChapterGenerationStarted?.(response.newChapterId, response.requestId);
    } catch (error) {
      console.error('Error generating chapter:', error);

      // Show toast notification instead of inline error
      toast.error('Unable to create your chapter. Please try again.');

      await trackEvent(AnalyticsEvent.CHAPTER_BUILDER_GENERATION_FAILED, {
        source: 'ChapterBuilderInlineView',
        book: book,
        chapter: lastChapter,
        error: error?.toString() || 'Unknown Error',
      });

      setIsGenerating(false);

      // Call the error handler callback
      onChapterGenerationError?.();
    }
  }

  if (isDilemmasLoading || !dilemmas) {
    return <LoadingIcon />;
  }

  return (
    <div className="flex flex-col gap-2 p-4 bg-white rounded-xl">
      <h2 className="text-2xl font-bold">What Happens Next?</h2>
      <p className="text-gray-600">
        Choose how the next chapter of your adventure unfolds!
      </p>

      {dilemmas.map((dilemma: StorylineDilemma) => (
        <div key={dilemma.id} className="space-y-4 pt-2">
          <StorylineDilemmaOptionsSelectView
            gridCss={'grid-cols-2 gap-3'}
            dilemma={dilemma}
            onOptionSelect={(optionId) => {
              handleOptionSelect(dilemma.id, optionId);
            }}
            selectedOptionIds={[
              selectedDilemmaOptionByDilemmaId[dilemma.id],
            ].filter(Boolean)}
            allowCreate={allowCreate}
            showDilemmaDetails={false}
            maxSelectedOptions={1}
            minSelectedOptions={1}
          />
        </div>
      ))}

      <div className="mt-8 flex flex-col items-center gap-4">
        {preSubmitPrompt && (
          <div className="text-center">
            <h3 className="text-lg font-bold">{preSubmitPrompt.title}</h3>
            <p className="text-gray-600">{preSubmitPrompt.description}</p>
          </div>
        )}

        <Button
          onClick={onSubmit}
          variant="primary"
          className="w-full max-w-xs"
          disabled={!canSubmit}
          isInProgress={isGenerating}
          inProgressText={`Generating Chapter ${lastChapter.chapterNumber + 1}...`}
        >
          {submitButtonText}
        </Button>
      </div>
    </div>
  );
};

export default ChapterBuilderInlineView;
